/**
 * GuestDesktopSplitView Component
 *
 * Desktop split-layout wrapper for guest view:
 * - Left half: GuestMobileView (scrollable event cards)
 * - Right half: GuestEventsTable (read-only events list)
 *
 * Manages state synchronization between the two panels:
 * - Clicking a table row navigates the left panel
 * - Navigating in left panel highlights the table row
 *
 * Features:
 * - Event type filtering (Concerts, Sporting Events, Others)
 */

import React, { useState, useMemo, useEffect } from 'react';
import { GuestMobileView } from './GuestMobileView';
import { GuestEventsTable } from './GuestEventsTable';
import {
  EventTypeFilter,
  defaultEventTypeFilters,
  filterEventsByType,
  type EventTypeFilters,
} from './EventTypeFilter';
import type { HMCEvent } from '../hmcEventsStore';

interface GuestDesktopSplitViewProps {
  events: HMCEvent[];
  maxGuestTickets: number;
}

export const GuestDesktopSplitView: React.FC<GuestDesktopSplitViewProps> = ({
  events,
  maxGuestTickets,
}) => {
  // Event type filters - all selected by default
  const [filters, setFilters] = useState<EventTypeFilters>(defaultEventTypeFilters);

  // Filter events based on active filters
  const filteredEvents = useMemo(
    () => filterEventsByType(events, filters),
    [events, filters]
  );

  // Shared state for current event index (within filtered list)
  const [currentEventIndex, setCurrentEventIndex] = useState(0);

  // Reset index when filters change and current index is out of bounds
  useEffect(() => {
    if (currentEventIndex >= filteredEvents.length) {
      setCurrentEventIndex(Math.max(0, filteredEvents.length - 1));
    }
  }, [filteredEvents.length, currentEventIndex]);

  // Handle table row click - navigate to that event
  const handleTableRowSelect = (index: number) => {
    setCurrentEventIndex(index);
  };

  // Handle mobile view index change - update table highlighting
  const handleMobileIndexChange = (index: number) => {
    setCurrentEventIndex(index);
  };

  if (events.length === 0) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <p className="text-dark-muted">No events available</p>
      </div>
    );
  }

  return (
    <div className="guest-split-container">
      {/* Left Panel - Mobile View */}
      <div className="guest-split-left">
        <GuestMobileView
          events={filteredEvents}
          maxGuestTickets={maxGuestTickets}
          currentIndex={currentEventIndex}
          onIndexChange={handleMobileIndexChange}
          isInSplitView={true}
          filterHeader={
            <EventTypeFilter
              filters={filters}
              onChange={setFilters}
              compact={false}
            />
          }
        />
      </div>

      {/* Right Panel - Events Table */}
      <div className="guest-split-right">
        <GuestEventsTable
          events={filteredEvents}
          currentEventIndex={currentEventIndex}
          onEventSelect={handleTableRowSelect}
          maxGuestTickets={maxGuestTickets}
          totalEventsCount={events.length}
        />
      </div>
    </div>
  );
};

export default GuestDesktopSplitView;
